'use client'

import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { useAuth, useSignIn } from '@clerk/nextjs'
import type { Role } from '@nivex/contracts'
import { ROLE_HOME, roleFromSessionClaims } from '@/features/auth/lib/session'

const ROLE_LABEL: Record<Role, string> = {
  investor: 'an investor',
  staff: 'a staff',
  admin: 'an admin',
}

// The "wrong tab" wording only makes sense where sign-in is split into role-specific tabs
// (team-login-view.tsx's Staff/Admin tabs) — /login has no such tabs for an investor.
const ROLE_MISMATCH_SUFFIX: Record<Role, string> = {
  investor: '',
  staff: ' Please use the correct tab for Sign In.',
  admin: ' Please use the correct tab for Sign In.',
}

/**
 * Shared by every flow that can end with `signIn.finalize()` activating a staff/admin
 * session — team sign-in and the admin self-serve password reset alike — since both need the
 * exact same race-condition-safe role check afterward: reject and sign out if the account
 * that just completed isn't actually the expected role, rather than silently landing it on
 * the wrong dashboard.
 */
export function useFinalizeAsRole() {
  const router = useRouter()
  const { signIn } = useSignIn()
  const { signOut } = useAuth()

  return function finalizeAs(
    expectedRole: Role,
    onMismatch: (message: string) => void,
    // Existing staff/admin callers rely on this defaulting to '/team/login' — unchanged.
    // Investor callers (login-view.tsx, the investor reset-password step) pass '/login'.
    signOutRedirectUrl: Route = '/team/login',
  ) {
    return signIn.finalize({
      // Reads the role synchronously off `session.lastActiveToken` — already populated the
      // instant `finalize()` activates the session, no `getToken()` network call needed. This
      // matters beyond speed: an `await getToken(...)` here left a window where Clerk's own
      // `router.refresh()` (which it fires the moment the session activates) could re-run
      // `/team/login`'s own "already signed in" check first and redirect to the account's real
      // dashboard before this mismatch check ever resolved. Deciding synchronously, in the same
      // tick `navigate` runs, closes that window.
      navigate: async ({ session, decorateUrl }) => {
        if (session?.currentTask) return

        const actualRole = roleFromSessionClaims(session?.lastActiveToken?.jwt?.claims)
        if (actualRole !== expectedRole) {
          // Set the error before signing out — signing out first (the original bug) sent
          // the browser to Clerk's default afterSignOutUrl before the error state was ever
          // set. `redirectUrl` keeps sign-out on the page that started this attempt.
          onMismatch(
            `That account isn't ${ROLE_LABEL[expectedRole]} account.${ROLE_MISMATCH_SUFFIX[expectedRole]}`,
          )
          await signOut({ redirectUrl: signOutRedirectUrl })
          return
        }

        const url = decorateUrl(ROLE_HOME[expectedRole])
        if (url.startsWith('http')) window.location.href = url
        // decorateUrl returns a plain string (Clerk API, not aware of Next's typed
        // routes) — safe to assert since it's derived from the literal route passed in.
        else router.push(url as Route)
      },
    })
  }
}
